import React from "react"
import styled from "styled-components"
import ReactDOMServer from "react-dom/server"
import { TeaserHorizontal as Teaser } from "@bw/partials"
import { CmsImage } from "@bw/bits"
import { breakpoints } from "../../../theme"
// parse is used to ensure that HTML sent by the CMS is converted to React components
import parse from "html-react-parser"
import maplibregl from "!maplibre-gl"
import maplibreglWorker from "maplibre-gl/dist/maplibre-gl-csp-worker"
maplibregl.workerClass = maplibreglWorker

const MapWrapper = styled.div`
  height: 202px;
  width: 100%;
  position: relative;
  background-color: var(--page-color);

  @media (min-width: ${breakpoints.medium}px) {
    height: 334px;
  }

  .map {
    position: absolute;
    width: 100%;
    height: 100%;
    --text-color: #111;
  }

  .maplibregl-marker {
    cursor: pointer;

    &:hover {
      filter: saturate(1.2);
    }
  }

  .horizontal-teaser:focus {
    outline: none;
  }
`

const Map = ({ pois, filteredPois }) => {
  const mapContainer = React.useRef(null)
  const map = React.useRef(null)
  const [markers, setMarkers] = React.useState([])

  /**
   * Initialize the map
   */
  React.useEffect(() => {
    if (map.current) return //stops map from intializing more than once

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: `https://api.maptiler.com/maps/streets/style.json?key=${process.env.GATSBY_API_KEY_MAPTILER}`,
      center: [7.991249, 46.769968],
      zoom: 6,
      maxZoom: 18,
    }).addControl(new maplibregl.NavigationControl(), "top-right")
  }, [])

  React.useEffect(() => {
    setMarkers(
      pois.map(poi => {
        const excerpt = poi.getTeaserExcerpt()
        const image =
          poi.teaserImage?.localImage?.childImageSharp?.gatsbyImageData?.images
            ?.fallback?.src
        const popup = new maplibregl.Popup()
          .setHTML(
            ReactDOMServer.renderToStaticMarkup(
              <Teaser
                image={image ? <CmsImage src={image} alt={poi.title} /> : null}
                title={poi.title}
                excerpt={excerpt ? parse(excerpt) : null}
                to={poi.url}
              />
            )
          )
          .setMaxWidth("300px")

        return {
          id: poi.id,
          marker: new maplibregl.Marker({ color: "var(--primary)" })
            .setPopup(popup)
            .setLngLat([poi.longitude, poi.latitude]),
        }
      })
    )
  }, [pois])

  /**
   * Add markers
   */
  React.useEffect(() => {
    if (!map.current) {
      return
    }

    if (filteredPois.length === 0) {
      return
    }

    const activePois = filteredPois.map(p => p.id)
    const bounds = new maplibregl.LngLatBounds()

    filteredPois.forEach(function (p) {
      bounds.extend(new maplibregl.LngLat(p.longitude, p.latitude))
    })

    for (let i = 0; i < markers.length; i++) {
      if (activePois.includes(markers[i].id)) {
        markers[i].marker.addTo(map.current)
      } else {
        markers[i].marker.remove()
      }
    }
    map.current.fitBounds(bounds, { padding: 50 })
  }, [markers, filteredPois, map])

  return (
    <MapWrapper>
      <div ref={mapContainer} className="map" />
    </MapWrapper>
  )
}

export default Map
